import { ContentLoading } from '@/components/common/content-loading';
import { FacilityPoiDetailModal } from '@/components/feature/facility';
import {
    AllFloorsGuide,
    ChangeRegionButton,
    EmptyCategoryPoiItem,
    HorizontalCategoryList,
    HorizontalFloorList,
    HorizontalMapHeader,
    NavigationGuide,
    NearbyPoiListDropdown,
    NoCategoryPoiPopup,
    PoiDetailModal,
    PoiListPopup,
    SelectTransportationModeModal,
    ZoomControlBtnGroup,
} from '@/components/feature/floor-guide';
import { ReviewMenuHorizontal } from '@/components/feature/shop';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@/components/icons';
import { CATEGORY_ORDER, FOCUS_TO_OBJECT_PADDING, FOCUS_TO_OPTIONS, MAP_CONTRAST_COLOR, OBJECT_GROUP, POI_ID_DELIMITER } from '@/constants/floor-guide';
import { FloorGuideType, NavigationStatus } from '@/enums/floor-guide';
import { useAccessibilityTTS, useRestoreFocusOnClose } from '@/hooks';
import { useCalculateIconSize, useFloorGuideState, usePoiContentModalState, usePoiContentState } from '@/hooks/floor-guide';
import {
    getCategoryCodesForButton,
    useBuildingCategory,
    useConvertFloors,
    useFloor,
    useMapControl,
    useMarker,
    useMyCurrentLocation,
    usePoi,
    useRouteDirection,
    useRouteSimulation,
} from '@/hooks/map';
import { useFloorCategory } from '@/hooks/map/useFloorCategory';
import { useMap } from '@/hooks/map/useMap';
import { useMapEvents } from '@/hooks/map/useMapEvents';
import { useMapMove } from '@/hooks/map/useMapMove';
import { useMapZoomLevel } from '@/hooks/map/useMapZoomLevel';
import { useCurrentBuilding } from '@/hooks/useCurrentBuilding';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import { useInteraction } from '@/hooks/useInteraction';
import { useConfigStore, useModalStackStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { useMarkerStore } from '@/stores/markerStore';
import type { NearbyPoiItemType } from '@/types/map';
import {
    generateFloorName,
    getFloorLocalizedName,
    getFloorMapName,
    syncMapLanguage,
    getNearestAvailablePoiItem,
    getPoiItemByTitle,
    isNonEmptyObject,
    resetObjectStyle,
    updateObjectGroup,
    updateObjectStyle,
} from '@/utils/floor-guide';
import {
    buildAllStoreFloors,
    filterCategoryPoisByStoreFloors,
    findNearestCategoryStoreFloor,
    findNearestFloorWithPoi,
    generateQrHereUrl,
    isMultiBuildingStore,
    getPoiObjectIds,
    getStoreFloorCode,
    getStoreFloorKoName,
    getStoreFloorsByBuildingMapId,
    isStoreFloorWithBuilding,
    safeMoveToMarker,
    waitForMarkerReady,
} from '@/utils/map';
import {
    addDistanceToPoiList,
    calculateEndPointFromPoi,
    calculateGetCurrentPoiOptions,
    getCurrentPois,
    getPoiById,
    resolveCategoryNearbyListHeader,
    sortNearbyPoiListByDistance,
    waitForPoisToLoad,
} from '@/utils/map-poi';
import { mapPoiListToMarker, mapPoiToMarker } from '@/utils/marker';
import { isValidNumber } from '@/utils/number';
import { getPoiBuildingName } from '@/utils/duty-free';
import { generateDisplayDirections, generateGetRouteOptions } from '@/utils/route-simulation';
import clsx from 'clsx';
import { FOCUS_TYPE, type TRANSPORTATION_REQUEST_TYPE } from 'dabeeomaps';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import type { INaviAnimOption } from 'dabeeomaps/dist/src/model/map/INaviAnimOption';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { JAMSIL_MAP_IDS } from '@/constants/app';

export default function HorizontalView() {
    const { i18n, t } = useTranslation();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const didMountRef = useRef(false);
    const processedPoiRef = useRef<string | null>(null);
    const processedParkingRef = useRef<string | null>(null);
    const pendingMarkerIdsRef = useRef<string[]>([]);
    const lastMarkerTimestampRef = useRef<number>(0);

    const { speak } = useAccessibilityTTS();

    const { selectedPoi, floorGuideType, selectedParkingSlot, resetSelectedPoi, resetFloorGuideType, resetSelectedParkingSlot } = useFloorGuideStore();
    const {
        state,
        modalState,
        nearbyState,
        handleSetModalState,
        handleSetState,
        handleSetNearbyState,
        handleGetBuildingNameById,
        handleResetNearbyState,
        handleGetPoiContentById,
    } = useFloorGuideState();

    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const language = useConfigStore((state) => state.config.ui.language);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const zoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId) || '';
    const isJamsil = JAMSIL_MAP_IDS.includes(mapId);

    const { markerIds, setMarkerIds } = useMarkerStore();
    const { resetCountdown } = useGlobalCountdownReset();

    const { restoreFocus } = useRestoreFocusOnClose({ active: modalState.noPoiCategory });

    const { initMap, cleanupMap, mapData, currentMap, isMapReady, loading } = useMap({
        clientId: server?.store?.mapClientId ?? '',
        clientSecret: server?.store?.mapSecret ?? '',
        studioVersion: 4,
    });

    const cleanupMapRef = useRef(cleanupMap);

    // Update the ref whenever cleanupMap changes
    cleanupMapRef.current = cleanupMap;

    const {
        modalState: poiDetailModalState,
        handleSetModalState: handleSetPoiDetailModalState,
        startPoint,
        endPoint,
        setEndPointFromPoi,
        navigationOptions,
        walkingOptions,
        routes,
        selectedRoute,
        selectedEndPointId,
        poiItemBySelectedEndPointId,
        generateNavigationGuideEndPoint,
        currentPoiOptions,
        setRoutes,
        setSelectedRoute,
        handlePoiClick,
        handleResetModalState,
        setDestinationDisplayName,
    } = usePoiContentState(mapData, currentMap);

    const {
        floors,
        selectedFloorName,
        handleChangeFloorByName,
        selectedFloor: selectedFloorItem,
        getFloorById,
        setSelectedFLoorName,
        getFloorsByBuildingId,
        handleChangeFloor,
    } = useFloor(currentMap, mapData);
    const [sortedFloors, setSortedFloors] = useState<IDataFloor[]>([]);
    const { handleZoomIn, handleZoomOut, isDisabledZoomIn, isDisabledZoomOut, setZoomLevel } = useMapZoomLevel(currentMap);
    const { selectedCategory, selectedCategoryCodes, selectBuildingCategory, sortedBuildingCategories, getCategoriesByBuildingId } = useBuildingCategory();
    const { addMarker, clearMarker } = useMarker(currentMap);
    const { groupedPoiByFloorId } = usePoi(currentMap, mapData);
    const { currentBuilding } = useCurrentBuilding();
    const { facilityPoiState, shopPoiState, fnbPoiState, fnbReviewList, loadingFnb, fnbMenuList, relatedPoiContentList, setSelectedPoiContent } =
        usePoiContentModalState(selectedEndPointId, selectedFloorItem);
    const { calculateIconSize } = useCalculateIconSize();
    const { handleFocusTo, handleMoveTo } = useMapControl(currentMap);

    const { addMyCurrentLocation } = useMyCurrentLocation(currentMap);
    const { setPoi, resetPoi } = usePoi(currentMap);
    const { getRouteDirection } = useRouteDirection(mapData);
    const { drawRouteSimulation, startRouteSimulation, pauseRouteSimulation, stopRouteSimulation, resumeRouteSimulation, clearRouteSimulation } =
        useRouteSimulation(currentMap);
    const { moveUp, moveDown, moveLeft, moveRight } = useMapMove(currentMap);

    const allPoiItems = useMemo(() => {
        if (!buildings) {
            return [];
        }
        return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
    }, [buildings]);

    const [currentMapBuildingId, setCurrentMapBuildingId] = useState<string | undefined>(currentBuilding?.studioBuildingId?.includes('_') ? currentBuilding?.studioBuildingId : `${currentBuilding?.studioBuildingId}_${currentBuilding?.storeCode}`);

    const isMultiBuilding = useMemo(() => isMultiBuildingStore(buildings), [buildings]);
    const useMultiBuildingCategoryLogic = isMultiBuilding && !isJamsil;

    const allStoreFloors = useMemo(
        () => (useMultiBuildingCategoryLogic ? buildAllStoreFloors(buildings) : []),
        [useMultiBuildingCategoryLogic, buildings]
    );

    const activeBuildingMapId = useMemo(() => {
        if (!useMultiBuildingCategoryLogic) {
            return '';
        }
        return (
            currentMapBuildingId ||
            (currentBuilding?.studioBuildingId?.includes('_')
                ? currentBuilding?.studioBuildingId
                : `${currentBuilding?.studioBuildingId}_${currentBuilding?.storeCode}`) ||
            ''
        );
    }, [useMultiBuildingCategoryLogic, currentMapBuildingId, currentBuilding?.studioBuildingId, currentBuilding?.storeCode]);

    const storeSortedFloors = useMemo(
        () => (useMultiBuildingCategoryLogic ? getStoreFloorsByBuildingMapId(buildings, activeBuildingMapId) : []),
        [useMultiBuildingCategoryLogic, buildings, activeBuildingMapId]
    );

    const { selectedFloor } = useFloorCategory(selectedFloorName, currentMapBuildingId);


    useEffect(() => {
        setSortedFloors(getFloorsByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || ''));
    }, [currentMapBuildingId, getFloorsByBuildingId]);

    useEffect(() => {
        if (!selectedPoi) return;
        setCurrentMapBuildingId(selectedPoi?.buildingId);
    }, [selectedPoi, mapData?.dataBuilding])


    const getPoiItemByStudioPoiId = useCallback(
        (studioPoiId: string) => {
            if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                return undefined;
            }

            return allPoiItems.find((item) => item?.studioPoiId === studioPoiId);
        },
        [allPoiItems]
    );

    const handleStopSimulation = async () => {
        stopRouteSimulation();
        clearRouteSimulation();
        handleSetModalState({ navigationGuide: false });
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();
        resetSelectedParkingSlot();
        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }
        setCurrentMapBuildingId(currentBuilding?.studioBuildingId?.includes('_') ? currentBuilding?.studioBuildingId : `${currentBuilding?.studioBuildingId}_${currentBuilding?.storeCode}`);

        await addMyCurrentLocation();
    };

    const handleSelectCategory = async (buildingCategory: any) => {
        // Create unique timestamp for this call
        const currentTimestamp = Date.now();
        lastMarkerTimestampRef.current = currentTimestamp;

        setSortedFloors(getFloorsByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || ''));

        // clear route simulation
        handleSetModalState({ nearbyPoiList: false, navigationGuide: false });
        handleResetNearbyState();
        stopRouteSimulation();
        clearRouteSimulation();
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();

        // Clear pending markers from previous calls that haven't completed yet
        if (Array.isArray(pendingMarkerIdsRef.current) && pendingMarkerIdsRef.current?.length > 0) {
            clearMarker(pendingMarkerIdsRef.current);
            pendingMarkerIdsRef.current = [];
        }

        // Clear existing markers
        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
        }

        if (markerIds?.selectedPoi) {
            clearMarker(markerIds?.selectedPoi);
        }

        // Reset marker state immediately
        setMarkerIds({ poiList: undefined, selectedPoi: undefined });

        // Small delay to ensure markers are cleared before adding new ones
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!buildingCategory?.clientCode) {
            return;
        }

        const categoriesCode = getCategoryCodesForButton(buildingCategory);

        let poiList = categoriesCode.flatMap((item) => {
            const poi = groupedPoiByFloorId.get(selectedFloorName)?.get(item);
            return poi ? poi : [];
        });

        if (useMultiBuildingCategoryLogic) {
            const poiListOnCurrentBuilding = filterCategoryPoisByStoreFloors(poiList, storeSortedFloors);

            if (!poiListOnCurrentBuilding.length) {
                const nearestFloor = findNearestCategoryStoreFloor(
                    selectedFloorName,
                    storeSortedFloors,
                    groupedPoiByFloorId,
                    categoriesCode,
                    allStoreFloors
                );
                handleSetState({ nearestPoiFloor: nearestFloor });
                setMarkerIds({ poiList: undefined, selectedPoi: undefined });
                selectBuildingCategory(buildingCategory);
                if (nearestFloor) {
                    handleSetModalState({ noPoiCategory: true });
                }
                return;
            }
        } else if (!poiList?.length) {
            const nearestFloor = findNearestFloorWithPoi(sortedFloors, selectedFloorName, groupedPoiByFloorId, categoriesCode);
            handleSetState({ nearestPoiFloor: nearestFloor });
            setMarkerIds({ poiList: undefined, selectedPoi: undefined });
            if (nearestFloor) {
                handleSetModalState({ noPoiCategory: true });
            }
        }

        // NEARBY

        if (Array.isArray(poiList) && poiList?.length) {
            const nearbyPoiList: NearbyPoiItemType[] = [];
            for (const poi of poiList) {
                const closestPoiOptions: IClosestPoiDataOption = {
                    floorId: poi?.floorId ?? '',
                    position: {
                        x: poi?.position?.x ?? 0,
                        y: poi?.position?.y ?? 0,
                    },
                };

                const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);
                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }

            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);

            const buildingName = handleGetBuildingNameById(currentMapBuildingId || currentBuilding?.studioBuildingId || '');

            if (sortedNearbyPoiList?.length) {
                const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                if (useMultiBuildingCategoryLogic) {
                    const fallbackBuildingName = buildingName ?? '';
                    const categoryHeader = resolveCategoryNearbyListHeader(sortedNearbyPoiList[0], {
                        buildings,
                        language,
                        mapData,
                        getPoiContentById: handleGetPoiContentById,
                        getBuildingNameByMapId: handleGetBuildingNameById,
                        getFloorById,
                        fallbackBuildingName,
                        fallbackFloorName: selectedFloorName ?? '',
                    });
                    handleSetNearbyState({
                        nearbyPoiList: sortedNearbyPoiList,
                        selectedPoiId: selectedPoiId,
                        title: buildingCategory?.name?.[language] || buildingCategory?.name?.['ko'] || '',
                        floorName: categoryHeader.floorName,
                        building: categoryHeader.buildingName,
                        type: 'category',
                    });
                } else {
                    handleSetNearbyState({
                        nearbyPoiList: sortedNearbyPoiList,
                        selectedPoiId: selectedPoiId,
                        title: buildingCategory?.name?.[language] || buildingCategory?.name?.['ko'] || '',
                        floorName: selectedFloorName ?? '',
                        building: buildingName ?? '',
                        type: 'category',
                    });
                }
                handleSetModalState({ nearbyPoiList: true });
            }
        }

        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }));
        const newMarkerIds = await addMarker({ marker: markers });

        // Check again if this is still the latest call after async operation
        if (currentTimestamp !== lastMarkerTimestampRef.current) {
            // Newer call has been made, clean up markers we just added
            if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
                clearMarker(newMarkerIds);
            }
            return;
        }

        // Store marker IDs in ref for cleanup in next call
        if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
            pendingMarkerIdsRef.current = newMarkerIds;
        }

        if (markers?.length) {
            speak(t('floor_guide.text_to_speech.click.location_this_floor', { number: markers?.length }));
        }

        setMarkerIds({ poiList: newMarkerIds });
        selectBuildingCategory(buildingCategory);

        const objectIds = getPoiObjectIds(poiList);

        if (!objectIds?.length) {
            return;
        }

        if (objectIds.length > 1) {
            handleFocusTo({
                transition: true,
                focus: {
                    type: FOCUS_TYPE.OBJECT,
                    ids: objectIds,
                },
                padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
            });
            return;
        }

        handleMoveTo({
            position: {
                x: poiList[0]?.position?.x ?? 0,
                y: poiList[0]?.position?.y ?? 0,
            },
            floorId: poiList[0]?.floorId,
            transition: true,
        });
    };

    const handleGoToFloor = async () => {
        if (!state.nearestPoiFloor) {
            return;
        }

        if (!useMultiBuildingCategoryLogic) {
            const mapNearestFloor = state.nearestPoiFloor as IDataFloor;
            const selectedFloorNameFromNearest = mapNearestFloor.name[0].text;

            if (!selectedFloorNameFromNearest) {
                return;
            }

            if (!isJamsil) {
                await handleChangeFloorByName(selectedFloorNameFromNearest);
            } else {
                await handleChangeFloor(mapNearestFloor.id);
            }

            if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
                clearMarker(markerIds?.poiList);
                setMarkerIds({ poiList: undefined });
            }

            if (!selectedCategory) {
                return;
            }

            const categoriesCode = selectedCategoryCodes.length > 0 ? selectedCategoryCodes : getCategoryCodesForButton(selectedCategory);

            const poiList = categoriesCode.flatMap((item) => {
                const poi = groupedPoiByFloorId.get(selectedFloorNameFromNearest)?.get(item);
                return poi ? poi : [];
            });

            if (!Array.isArray(poiList) || !poiList?.length) {
                return;
            }

            const nearbyPoiList: NearbyPoiItemType[] = [];
            for (const poi of poiList) {
                const closestPoiOptions: IClosestPoiDataOption = {
                    floorId: poi?.floorId ?? '',
                    position: {
                        x: poi?.position?.x ?? 0,
                        y: poi?.position?.y ?? 0,
                    },
                };

                const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);

                if (Array.isArray(orderedPoiList) && orderedPoiList?.length > 0) {
                    nearbyPoiList.push({
                        ...poi,
                        nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                    });
                }
            }

            if (nearbyPoiList?.length) {
                const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
                const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);
                const buildingName = handleGetBuildingNameById(currentMapBuildingId || currentBuilding?.studioBuildingId || '');

                if (sortedNearbyPoiList?.length) {
                    const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                    handleSetNearbyState({
                        nearbyPoiList: sortedNearbyPoiList,
                        selectedPoiId: selectedPoiId,
                        title: selectedCategory?.name?.[language] || selectedCategory?.name?.['ko'] || '',
                        floorName: selectedFloorNameFromNearest ?? '',
                        building: buildingName ?? '',
                        type: 'category',
                    });
                    handleSetModalState({ nearbyPoiList: true });
                }
            }

            const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }));
            const newMarkerIds = await addMarker({ marker: markers });

            setMarkerIds({ poiList: newMarkerIds });
            handleSetModalState({ noPoiCategory: false });

            const objectIds = getPoiObjectIds(poiList);

            if (!objectIds?.length) {
                return;
            }

            if (objectIds.length > 1) {
                setTimeout(() => {
                    handleFocusTo({
                        transition: true,
                        focus: {
                            type: FOCUS_TYPE.OBJECT,
                            ids: objectIds,
                        },
                        padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
                    });
                }, 100);
                return;
            }

            setTimeout(() => {
                handleMoveTo({
                    position: {
                        x: poiList[0]?.position?.x ?? 0,
                        y: poiList[0]?.position?.y ?? 0,
                    },
                    floorId: poiList[0]?.floorId,
                    transition: true,
                });
            }, 100);
            return;
        }

        const storeNearestFloor = state.nearestPoiFloor;
        if (!isStoreFloorWithBuilding(storeNearestFloor)) {
            return;
        }

        const floorMapName = getFloorMapName(storeNearestFloor);
        const selectedFloorLabel = getFloorLocalizedName(storeNearestFloor, language) || floorMapName;

        if (!floorMapName && !selectedFloorLabel) {
            return;
        }

        const targetBuildingId = storeNearestFloor.buildingId;
        const isCrossBuilding = Boolean(targetBuildingId && targetBuildingId !== activeBuildingMapId);

        if (isCrossBuilding && targetBuildingId) {
            setCurrentMapBuildingId(targetBuildingId);
        }

        if (!isJamsil) {
            await handleChangeFloorByName(floorMapName);
            if (isCrossBuilding) {
                await handleChangeFloor(getStoreFloorCode(storeNearestFloor));
            }
        } else {
            await handleChangeFloor(getStoreFloorCode(storeNearestFloor));
        }

        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
            setMarkerIds({ poiList: undefined });
        }

        if (!selectedCategory) {
            return;
        }

        const categoriesCode = selectedCategoryCodes.length > 0 ? selectedCategoryCodes : getCategoryCodesForButton(selectedCategory);

        const poiFloorKey = !isJamsil ? floorMapName : selectedFloorLabel;

        const rawPoiList = categoriesCode.flatMap((item) => {
            const poi = groupedPoiByFloorId.get(poiFloorKey)?.get(item);
            return poi ? poi : [];
        });

        const targetStoreFloors = getStoreFloorsByBuildingMapId(buildings, targetBuildingId || activeBuildingMapId);
        const poiList = filterCategoryPoisByStoreFloors(rawPoiList, targetStoreFloors);

        if (!Array.isArray(poiList) || !poiList?.length) {
            return;
        }

        // NEARBY
        const nearbyPoiList: NearbyPoiItemType[] = [];
        for (const poi of poiList) {
            const closestPoiOptions: IClosestPoiDataOption = {
                floorId: poi?.floorId ?? '',
                position: {
                    x: poi?.position?.x ?? 0,
                    y: poi?.position?.y ?? 0,
                },
            };

            const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);

            if (Array.isArray(orderedPoiList) && orderedPoiList?.length > 0) {
                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }
        }

        if (nearbyPoiList?.length) {
            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);
            const fallbackBuildingName =
                handleGetBuildingNameById(targetBuildingId || currentMapBuildingId || currentBuilding?.studioBuildingId || '') ?? '';

            if (sortedNearbyPoiList?.length) {
                const categoryHeader = resolveCategoryNearbyListHeader(sortedNearbyPoiList[0], {
                    buildings,
                    language,
                    mapData,
                    getPoiContentById: handleGetPoiContentById,
                    getBuildingNameByMapId: handleGetBuildingNameById,
                    getFloorById,
                    fallbackBuildingName,
                    fallbackFloorName: selectedFloorLabel ?? '',
                });
                const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                handleSetNearbyState({
                    nearbyPoiList: sortedNearbyPoiList,
                    selectedPoiId: selectedPoiId,
                    title: selectedCategory?.name?.[language] || selectedCategory?.name?.['ko'] || '',
                    floorName: categoryHeader.floorName,
                    building: categoryHeader.buildingName,
                    type: 'category',
                });
                handleSetModalState({ nearbyPoiList: true });
            }
        }

        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }));
        const newMarkerIds = await addMarker({ marker: markers });

        setMarkerIds({ poiList: newMarkerIds });
        handleSetModalState({ noPoiCategory: false });

        const objectIds = getPoiObjectIds(poiList);

        if (!objectIds?.length) {
            return;
        }

        if (objectIds.length > 1) {
            setTimeout(() => {
                handleFocusTo({
                    transition: true,
                    focus: {
                        type: FOCUS_TYPE.OBJECT,
                        ids: objectIds,
                    },
                    padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
                });
            }, 100);
            return;
        }

        setTimeout(() => {
            handleMoveTo({
                position: {
                    x: poiList[0]?.position?.x ?? 0,
                    y: poiList[0]?.position?.y ?? 0,
                },
                floorId: poiList[0]?.floorId,
                transition: true,
            });
        }, 100);
    };

    const handleCloseNearbyPoiList = async () => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();

        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId?.includes('_') ? currentBuilding?.studioBuildingId : `${currentBuilding?.studioBuildingId}_${currentBuilding?.storeCode}`);
        await addMyCurrentLocation();
    };

    const handleStart = useCallback(
        (animationOptions?: INaviAnimOption) => {
            startRouteSimulation(animationOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });
        },
        [startRouteSimulation, handleSetState]
    );

    const handleStartNavigationGuide = async (poi: IPoi) => {
        // Set endpoint - no timing issues since it's direct state!
        const displayName = nearbyState?.type === 'show-location' ? nearbyState?.title : undefined;
        setEndPointFromPoi(poi, displayName);

        // Calculate endpoint for immediate use
        const calculatedEndPoint = calculateEndPointFromPoi(poi, mapData);

        // Use immediately
        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, calculatedEndPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMarker(markerIds.myLocation ?? '');
        setMarkerIds({ myLocation: undefined });

        setRoutes(routeDirections);

        if (startPoint?.floorName !== calculatedEndPoint.floorName) {
            handleSetModalState({ selectTransportationMode: true });
            handleResetModalState();
            return;
        }

        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        handleResetModalState();
        handleSetModalState({ navigationGuide: true });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleStartGuide = async (destinationName?: string) => {
        if (!endPoint) return;
        if (destinationName) {
            setDestinationDisplayName(destinationName);
        }
        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, endPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMarker(markerIds.myLocation ?? '');
        setMarkerIds({ myLocation: undefined });

        setRoutes(routeDirections);
        if (startPoint?.floorName !== endPoint?.floorName) {
            handleSetModalState({ selectTransportationMode: true, poiList: false });
            handleResetModalState();
            return;
        }
        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, poiList: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleSelectTransportationMode = async (mode: TRANSPORTATION_REQUEST_TYPE) => {
        if (!routes || !Object.keys(routes)?.length || !routes[mode]) {
            return;
        }
        setSelectedRoute(routes[mode]);
        await drawRouteSimulation(routes[mode], navigationOptions);
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, selectTransportationMode: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleResumeSimulation = () => {
        resumeRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handlePauseSimulation = () => {
        pauseRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.PAUSED });
    };

    const handleWatchAgain = () => {
        startRouteSimulation(walkingOptions);
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handleShowMyLocation = async (type?: 'show-location') => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();
        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        if (!isJamsil) {
            await handleChangeFloorByName(startPoint?.floorName ?? '');
        } else {
            await handleChangeFloor(startPoint?.floorId ?? '');
        }

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId?.includes('_') ? currentBuilding?.studioBuildingId : `${currentBuilding?.studioBuildingId}_${currentBuilding?.storeCode}`);


        if (type === 'show-location') {
            const currentPoiList = getCurrentPois(mapData, currentPoiOptions);
            if (Array.isArray(currentPoiList) && currentPoiList?.length && currentPoiList[0]?.id) {
                const poiItem = getPoiItemByStudioPoiId(currentPoiList[0]?.id);
                if (poiItem) {
                    speak(
                        `${t('floor_guide.text_to_speech.current_location', { floorName: generateFloorName(startPoint?.floorName ?? ''), poiName: poiItem?.name?.[language] || poiItem?.name?.['ko'] || '' })}`
                    );
                }
            }
        }
        await addMyCurrentLocation();
    };

    // Language must be applied first: changeLanguage redraws POIs and wipes the contrast style.
    const applyMapLanguageAndStyle = useCallback(async () => {
        if (!currentMap) return;
        await syncMapLanguage(currentMap, language);

        if (contrast) {
            updateObjectGroup(mapData, OBJECT_GROUP);
            updateObjectStyle(currentMap, OBJECT_GROUP);
            setPoi(MAP_CONTRAST_COLOR.POI);
        } else {
            resetObjectStyle(currentMap);
            resetPoi();
        }
    }, [currentMap, mapData, contrast, language, resetPoi, setPoi]);

    useMapEvents({
        mapContainer: mapContainerRef,
        onPoiClick: async (data) => {
            stopRouteSimulation();
            clearRouteSimulation();

            handleSetModalState({ navigationGuide: false, nearbyPoiList: false });

            setRoutes(undefined);
            handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
            resetSelectedPoi();
            resetFloorGuideType();
            handlePoiClick(data);
            handleResetNearbyState();
        },
        onNavigationComplete: async () => {
            handleSetState({ navigationStatus: NavigationStatus.DONE });
            setRoutes(undefined);
            setTimeout(() => {
                handleFocusTo(FOCUS_TO_OPTIONS);
            }, 100);
            if (endPoint?.floorName) {
                await handleChangeFloorByName(endPoint.floorName);
            }
        },
        onFloorChanged: (data) => {
            if (Array.isArray(data?.name) && data?.name?.length) {
                setSelectedFLoorName(data?.name[0]?.text);
            }
            void applyMapLanguageAndStyle();
        },
        onZoomChanged: (data) => {
            setZoomLevel(data?.changedValue ?? 1);
        },
    });

    useEffect(() => {
        initMap(mapContainerRef);
    }, [initMap]);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (!mapContainerRef.current) return;

        cleanupMap();

        // Single RAF is sufficient - ensures DOM is ready after cleanup
        const rafId = requestAnimationFrame(() => {
            initMap(mapContainerRef);
        });

        return () => {
            cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenMode]);

    useEffect(() => {
        void applyMapLanguageAndStyle();
    }, [applyMapLanguageAndStyle]);

    useEffect(() => {
        return () => {
            cleanupMapRef.current();
        };
    }, []); // Empty dependency array - only run on mount/unmount

    useEffect(() => {
        if (
            !selectedParkingSlot?.studioFloorID ||
            !selectedParkingSlot?.parkingSlotTitle ||
            floorGuideType !== FloorGuideType.DIRECTION_FROM_PARKING ||
            !mapData ||
            !isMapReady
        ) {
            return;
        }

        const run = async () => {
            const currentParkingKey = `${selectedParkingSlot.studioFloorID}-${selectedParkingSlot.parkingSlotTitle}-${floorGuideType}`;
            if (processedParkingRef.current === currentParkingKey) {
                return;
            }

            processedParkingRef.current = currentParkingKey;

            let poiItem: IPoi | IPoi[] | undefined;

            poiItem = mapData?.dataPoi?.find({
                title: selectedParkingSlot?.parkingSlotTitle,
                floorId: selectedParkingSlot?.studioFloorID,
            });

            if (!poiItem) {
                poiItem = mapData?.dataPoi?.find({
                    title: selectedParkingSlot?.parkingSlotTitle,
                });
            }
            
            const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === selectedPoi?.studioFloorID);
            const filteredPoiItem = getPoiItemByTitle(poiItem, selectedParkingSlot?.parkingSlotTitle);
            setEndPointFromPoi(filteredPoiItem);

            if (!filteredPoiItem) {
                processedParkingRef.current = null;
                return;
            }

            const destinationPoint = {
                floorId: filteredPoiItem?.floorId ?? '',
                floorName: floor?.name[0].text ?? '',
                position: {
                    x: filteredPoiItem?.position?.x ?? 0,
                    y: filteredPoiItem?.position?.y ?? 0,
                },
                poiId: filteredPoiItem?.id,
            };

            const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, destinationPoint));
            if (!routeDirections || !Object.keys(routeDirections)?.length) {
                processedParkingRef.current = null;
                return;
            }

            clearMarker(markerIds.myLocation ?? '');
            setMarkerIds({ myLocation: undefined });
            setRoutes(routeDirections);
            if (startPoint?.floorName !== destinationPoint.floorName) {
                handleSetModalState({ selectTransportationMode: true });
                handleResetModalState();
                // Reset state
                resetSelectedPoi();
                resetFloorGuideType();
                resetSelectedParkingSlot();
                processedParkingRef.current = null;
                return;
            }

            setSelectedRoute(routeDirections?.recommendation);
            await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
            handleResetModalState();
            handleSetModalState({ navigationGuide: true });
            handleStart(walkingOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });

            // reset state
            resetSelectedParkingSlot();
            resetFloorGuideType();
            processedParkingRef.current = null;
        };
        run();
    }, [
        currentMap,
        selectedParkingSlot,
        floorGuideType,
        isMapReady,
        startPoint,
        mapData?.dataPoi,
        mapData?.dataFloor,
        navigationOptions,
        walkingOptions,
        markerIds.myLocation,
        calculateIconSize,
        handleChangeFloorByName,
        getRouteDirection,
        handleSetModalState,
        handleResetModalState,
        setRoutes,
        resetSelectedPoi,
        resetSelectedParkingSlot,
        resetFloorGuideType,
        addMarker,
        handleStart,
        drawRouteSimulation,
        setSelectedRoute,
        clearMarker,
        setMarkerIds,
        handleSetState,
        setEndPointFromPoi,
        handleSetNearbyState,
        currentMap?.pois?.getCurrentPois,
        handleGetPoiContentById,
        language,
        selectedFloorName,
        handleGetBuildingNameById,
        mapData,
        selectedPoi?.studioFloorID,
    ]);

    useEffect(() => {
        const run = async () => {
            if (!selectedPoi?.studioFloorID || !selectedPoi?.studioPoiId || !floorGuideType || !isMapReady) {
                return;
            }

            // Kiểm tra xem POI này đã được xử lý chưa để tránh chạy lại
            const currentPoiKey = `${selectedPoi.studioPoiId}-${floorGuideType}`;
            if (processedPoiRef.current === currentPoiKey) {
                return;
            }

            // Đánh dấu đang xử lý POI này
            processedPoiRef.current = currentPoiKey;

            const poiItem = getPoiById(mapData?.dataPoi?.getPois(), selectedPoi.studioPoiId);
            const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === selectedPoi?.studioFloorID);
            setEndPointFromPoi(poiItem);

            if (selectedPoi && floorGuideType === FloorGuideType.MARKER) {
                const options = calculateGetCurrentPoiOptions(poiItem as IPoi);

                const currentPoiList = await waitForPoisToLoad(mapData, options, 'selectedPoi-MARKER');

                const nearbyPoiItem = getNearestAvailablePoiItem(currentPoiList, allPoiItems);

                const poiContent = handleGetPoiContentById(selectedPoi?.studioPoiId);

                const building = buildings?.find(
                    (b) => b.storeCode === poiContent?.contents?.cstrCd?.ko && b.buildingCode === poiContent?.contents?.cstrTownCd?.ko
                );
                const defaultBuildingName = building?.name?.[language] || building?.name?.['ko'] || '';
                const buildingName = poiContent ? getPoiBuildingName(poiContent, language, defaultBuildingName) : defaultBuildingName;

                if (Array.isArray(currentPoiList) && currentPoiList?.length) {
                    const POINearByCurrentPoi = nearbyPoiItem;
                    const selectedPoiId = `${poiItem?.id}${POI_ID_DELIMITER}${POINearByCurrentPoi?.id}`;
                    const displayName = selectedPoi.name || poiContent?.name?.[language] || poiContent?.name?.['ko'] || '';
                    setDestinationDisplayName(displayName);
                    handleSetNearbyState({
                        nearbyPoiList: [{ ...(poiItem as IPoi), nearbyPoi: POINearByCurrentPoi }],
                        selectedPoiId: selectedPoiId,
                        title: displayName,
                        floorName: floor?.name[0].text ?? '',
                        building: buildingName ?? '',
                        type: 'show-location',
                    });
                    handleSetModalState({ nearbyPoiList: true });
                }

                if (!isJamsil) {
                    await handleChangeFloorByName(floor?.name[0].text);
                } else {
                    await handleChangeFloor(floor?.id);
                }

                // add marker
                const poiMarker = mapPoiToMarker(poiItem, calculateIconSize({ width: 35, height: 48 }));
                const markerIds = await addMarker({ marker: poiMarker ? [poiMarker] : [] });

                let markerId: string | undefined;
                if (Array.isArray(markerIds) && markerIds?.length) {
                    markerId = markerIds[0];
                    setMarkerIds({ selectedPoi: markerId });
                }

                // Đợi và verify marker đã được vẽ trên map
                if (markerId && isValidNumber(poiItem?.position?.x) && isValidNumber(poiItem?.position?.y)) {
                    await waitForMarkerReady(currentMap, markerId, { x: poiItem?.position?.x, y: poiItem?.position?.y }, 10, 'MARKER-FloorGuide-Horizontal');

                    // Safe moveTo với retry logic
                    await safeMoveToMarker(
                        currentMap,
                        { x: poiItem?.position?.x ?? 0, y: poiItem?.position?.y ?? 0 },
                        poiItem?.floorId,
                        true,
                        3,
                        'MARKER-FloorGuide-Horizontal'
                    );
                }
                // reset State
                if (!isJamsil) {
                    resetSelectedPoi();
                }
                resetFloorGuideType();
                processedPoiRef.current = null;
                return;
            }

            // create route
            if (selectedPoi.name) {
                setDestinationDisplayName(selectedPoi.name);
            }
            const destinationPoint = {
                floorId: poiItem?.floorId ?? '',
                floorName: floor?.name[0].text ?? '',
                position: {
                    x: poiItem?.position?.x ?? 0,
                    y: poiItem?.position?.y ?? 0,
                },
                poiId: poiItem?.id,
            };

            const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, destinationPoint));
            if (!routeDirections || !Object.keys(routeDirections)?.length) {
                processedPoiRef.current = null;
                return;
            }

            clearMarker(markerIds.myLocation ?? '');
            setMarkerIds({ myLocation: undefined });
            setRoutes(routeDirections);
            if (startPoint?.floorName !== destinationPoint.floorName) {
                handleSetModalState({ selectTransportationMode: true });
                handleResetModalState();
                resetSelectedPoi();
                resetFloorGuideType();
                processedPoiRef.current = null;
                return;
            }

            setSelectedRoute(routeDirections?.recommendation);
            await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
            handleResetModalState();
            handleSetModalState({ navigationGuide: true });
            handleStart(walkingOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });

            // reset state
            resetSelectedPoi();
            resetFloorGuideType();
            processedPoiRef.current = null;
        };

        run();
    }, [
        currentMap,
        selectedPoi,
        floorGuideType,
        isMapReady,
        startPoint,
        mapData?.dataPoi,
        mapData?.dataFloor,
        navigationOptions,
        walkingOptions,
        markerIds.myLocation,
        calculateIconSize,
        handleChangeFloorByName,
        getRouteDirection,
        handleSetModalState,
        handleResetModalState,
        setRoutes,
        resetSelectedPoi,
        resetFloorGuideType,
        addMarker,
        handleStart,
        drawRouteSimulation,
        setSelectedRoute,
        clearMarker,
        setMarkerIds,
        handleSetState,
        setEndPointFromPoi,
        handleSetNearbyState,
        currentMap?.pois?.getCurrentPois,
        handleGetPoiContentById,
        language,
        selectedFloorName,
        handleGetBuildingNameById,
        mapData,
        allPoiItems,
        setDestinationDisplayName,
    ]);

    const disableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: false,
                enablePan: false,
            },
            touchOption: {
                enableZoom: false,
                enablePan: false,
            },
        });
    }, [currentMap]);

    const enableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: true,
                enablePan: true,
            },
            touchOption: {
                enableZoom: true,
                enablePan: true,
            },
        });
    }, [currentMap]);

    useEffect(() => {
        if (zoomClosed) {
            enableMouseEvent();
        } else {
            disableMouseEvent();
        }
    }, [zoomClosed, enableMouseEvent, disableMouseEvent]);

    useInteraction(containerRef, (eventType) => {
        // mouseleave also fires when an overlay (e.g. TimeExtensionModal) appears over a resting cursor, so it is not a user interaction
        if (eventType === 'mouseleave') return;

        // Don't reset countdown if TimeExtensionModal is open
        // Other modals should still allow countdown reset on interaction
        const { isModalInStack } = useModalStackStore.getState();
        const isTimeExtensionModalOpen = isModalInStack('time-extension-modal');

        if (!isTimeExtensionModalOpen) {
            resetCountdown();
        }
    });

    return (
        <section className={styles['horizontal-view']}>
            <section className={styles['horizontal-view__floor-list']}>
                <HorizontalFloorList
                    floors={sortedFloors}
                    selectedFloorName={selectedFloorName}
                    onSelectFloor={async (floorName: string) => {
                        handleSetModalState({ nearbyPoiList: false });
                        handleResetNearbyState();
                        handleSetState({ isShowAllFloorGuide: false });
                        stopRouteSimulation();
                        clearRouteSimulation();
                        handleSetModalState({ navigationGuide: false });
                        setRoutes(undefined);
                        handleSetState({ navigationStatus: NavigationStatus.DONE });
                        if (!isJamsil) {
                            resetSelectedPoi();
                        }
                        resetFloorGuideType();
                        if (markerIds?.selectedPoi) {
                            clearMarker(markerIds?.selectedPoi);
                        }
                        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
                            clearMarker(markerIds?.poiList);
                        }
                        setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                        await handleChangeFloorByName(floorName);
                    }}
                    isShowAllFloorGuide={state.isShowAllFloorGuide}
                    setIsShowAllFloorGuide={async (isShow: boolean) => {
                        handleSetModalState({ nearbyPoiList: false });
                        handleResetNearbyState();
                        handleSetState({ isShowAllFloorGuide: isShow });
                        handleSetModalState({ navigationGuide: false });
                        await handleStopSimulation();
                    }}
                    onShowMyLocation={async () => await handleShowMyLocation('show-location')}
                    currentMapBuildingId={currentMapBuildingId}
                />
            </section>
            <section className={styles['horizontal-view__content']}>
                <section
                    className={clsx(styles['horizontal-view__map-header'], {
                        [styles['horizontal-view__map-header--show']]: !state.isShowAllFloorGuide,
                        [styles['horizontal-view__map-header--hide']]: state.isShowAllFloorGuide,
                    })}
                >
                    <HorizontalMapHeader categoryOnFloor={selectedFloor?.lotteDescription} tabIndex={2} />
                </section>
                <div className={styles['horizontal-view__map-container']}>
                    <div
                        ref={containerRef}
                        className={clsx(
                            {
                                [styles['horizontal-view__map-container__show']]: !state.isShowAllFloorGuide,
                                [styles['horizontal-view__map-container__hide']]: state.isShowAllFloorGuide,
                            },
                            'h-full w-full'
                        )}
                        style={{
                            pointerEvents: zoomClosed ? 'auto' : 'none',
                        }}
                    >
                        <div className={clsx('h-full w-full')} ref={mapContainerRef} />
                    </div>
                    <section
                        className={clsx(styles['horizontal-view__zoom-control'], {
                            hidden: state.isShowAllFloorGuide,
                            block: !state.isShowAllFloorGuide,
                        })}
                    >
                        <ZoomControlBtnGroup
                            tabIndex={7}
                            disabledZoomIn={isDisabledZoomIn}
                            disabledZoomOut={isDisabledZoomOut}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                        />
                    </section>
                    {state.isShowAllFloorGuide && (
                        <AllFloorsGuide
                            onSelectFloor={async (floorName: string, floorId?: string, buildingId?: string, storeCode?: string) => {
                                handleSetState({ isShowAllFloorGuide: false });
                                await handleStopSimulation();
                                if (!isJamsil) {
                                    await handleChangeFloorByName(floorName);
                                } else {
                                    await handleChangeFloor(floorId);
                                    setCurrentMapBuildingId(buildingId + '_' + storeCode);
                                }
                                speak(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
                            }}
                        />
                    )}

                    {modalState.navigationGuide && screenMode === 'fullscreen' && (
                        <div className={clsx(styles['horizontal-view__navigation-guide'], styles['horizontal-view__navigation-guide--fullscreen'])}>
                            <NavigationGuide
                                isActive={modalState.navigationGuide}
                                isShowPauseBtn={state.navigationStatus === NavigationStatus.RUNNING || state.navigationStatus === NavigationStatus.IDLE}
                                isShowResumeBtn={state.navigationStatus === NavigationStatus.PAUSED}
                                isShowWatchAgainBtn={state.navigationStatus === NavigationStatus.DONE}
                                qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, endPoint?.poiId ?? '')}
                                buildingName={currentBuilding?.name?.[i18n.language] ?? ''}
                                directions={generateDisplayDirections(
                                    t,
                                    (floorId: string) => {
                                        const floorName = Array.isArray(getFloorById(floorId)?.name) ? (getFloorById(floorId)?.name?.[0].text ?? '') : '';
                                        return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                    },
                                    selectedRoute?.navigationList,
                                    generateNavigationGuideEndPoint.poiName ||
                                        poiItemBySelectedEndPointId?.name?.[language] ||
                                        poiItemBySelectedEndPointId?.name?.['ko'] ||
                                        '',
                                    (buildingId?: string) => {
                                        if (!Array.isArray(buildings) || !buildings?.length) {
                                            return '';
                                        }
                                        const building = buildings.find((building) => building?.studioBuildingId === buildingId);
                                        return building?.name?.[language] || building?.name?.['ko'] || '';
                                    }
                                )}
                                startFloorName={server?.floor?.name?.[i18n.language] || server?.floor?.name?.['ko'] || ''}
                                endPoint={generateNavigationGuideEndPoint}
                                onStop={handleStopSimulation}
                                onResume={handleResumeSimulation}
                                onPause={handlePauseSimulation}
                                onWatchAgain={handleWatchAgain}
                            />
                        </div>
                    )}
                    {modalState.nearbyPoiList && (
                        <div className={styles['horizontal-view__nearby-poi-list-dropdown']}>
                            <NearbyPoiListDropdown
                                type={nearbyState?.type}
                                isActive={modalState.nearbyPoiList}
                                poiList={nearbyState.nearbyPoiList}
                                selectedPoiId={nearbyState.selectedPoiId}
                                title={nearbyState?.title ?? ''}
                                floorName={nearbyState?.floorName ?? ''}
                                buildingName={nearbyState?.building ?? ''}
                                mapData={useMultiBuildingCategoryLogic ? mapData : undefined}
                                getFloorById={useMultiBuildingCategoryLogic ? getFloorById : undefined}
                                getPoiContentById={useMultiBuildingCategoryLogic ? handleGetPoiContentById : undefined}
                                getBuildingNameByMapId={useMultiBuildingCategoryLogic ? handleGetBuildingNameById : undefined}
                                resolveCategoryHeaderByPoi={useMultiBuildingCategoryLogic}
                                onSelectPoi={(poi) => {
                                    const selectedPoiId = `${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}`;
                                    if (!useMultiBuildingCategoryLogic) {
                                        handleSetNearbyState({ selectedPoiId });
                                        return;
                                    }
                                    const fallbackBuildingName =
                                        handleGetBuildingNameById(currentMapBuildingId || currentBuilding?.studioBuildingId || '') ?? '';
                                    const header = resolveCategoryNearbyListHeader(poi, {
                                        buildings,
                                        language,
                                        mapData,
                                        getPoiContentById: handleGetPoiContentById,
                                        getBuildingNameByMapId: handleGetBuildingNameById,
                                        getFloorById,
                                        fallbackBuildingName,
                                        fallbackFloorName: nearbyState?.floorName ?? selectedFloorName ?? '',
                                    });
                                    handleSetNearbyState({
                                        selectedPoiId,
                                        building: header.buildingName,
                                        floorName: header.floorName,
                                    });
                                }}
                                onStartNavigationGuide={async (poi) => {
                                    handleSetModalState({ nearbyPoiList: false });
                                    const poiListMarkerIds = Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length ? markerIds?.poiList : [];
                                    const clearMarkerIds = [markerIds?.selectedPoi ?? '', ...poiListMarkerIds];
                                    clearMarker(clearMarkerIds);
                                    setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                                    await handleStartNavigationGuide(poi as IPoi);
                                }}
                                onClose={handleCloseNearbyPoiList}
                            />
                        </div>
                    )}
                    <div data-region-nav={!state.isShowAllFloorGuide && !zoomClosed} data-region-order={10}>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--up'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={8}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.up')}
                                icon={<ChevronUpIcon />}
                                onClick={moveUp}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--left'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={8}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.left')}
                                icon={<ChevronLeftIcon />}
                                onClick={moveLeft}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--down'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={8}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.down')}
                                icon={<ChevronDownIcon />}
                                onClick={moveDown}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--right'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={8}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.right')}
                                icon={<ChevronRightIcon />}
                                onClick={moveRight}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                    </div>
                </div>
                <section
                    className={clsx(styles['horizontal-view__category-list'], {
                        [styles['horizontal-view__category-list--show']]: !state.isShowAllFloorGuide,
                        [styles['horizontal-view__category-list--hide']]: state.isShowAllFloorGuide,
                    })}
                >
                    <HorizontalCategoryList
                        buildingCategories={!isJamsil ? sortedBuildingCategories : getCategoriesByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || '').sort((a, b) => {
                            const orderA = CATEGORY_ORDER[a.clientCode] ?? Infinity;
                            const orderB = CATEGORY_ORDER[b.clientCode] ?? Infinity;
                            return orderA - orderB;
                        })}
                        onShowPoiList={() => {
                            handleSetModalState({ poiList: !modalState.poiList });
                            handleSetModalState({ nearbyPoiList: false });
                            handleResetNearbyState();
                        }}
                        onSelectCategory={handleSelectCategory}
                    />
                </section>
                {modalState.navigationGuide && screenMode !== 'fullscreen' && (
                    <div className={clsx(styles['horizontal-view__navigation-guide'])}>
                        <NavigationGuide
                            isActive={modalState.navigationGuide}
                            isShowPauseBtn={state.navigationStatus === NavigationStatus.RUNNING || state.navigationStatus === NavigationStatus.IDLE}
                            isShowResumeBtn={state.navigationStatus === NavigationStatus.PAUSED}
                            isShowWatchAgainBtn={state.navigationStatus === NavigationStatus.DONE}
                            qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, endPoint?.poiId ?? '')}
                            buildingName={currentBuilding?.name?.[i18n.language] || currentBuilding?.name?.['ko'] || ''}
                            directions={generateDisplayDirections(
                                t,
                                (floorId: string) => {
                                    const floorName = Array.isArray(getFloorById(floorId)?.name) ? (getFloorById(floorId)?.name?.[0].text ?? '') : '';
                                    return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                },
                                selectedRoute?.navigationList,
                                generateNavigationGuideEndPoint.poiName ||
                                    poiItemBySelectedEndPointId?.name?.[language] ||
                                    poiItemBySelectedEndPointId?.name?.['ko'] ||
                                    '',
                                (buildingId?: string) => {
                                    if (!Array.isArray(buildings) || !buildings?.length) {
                                        return '';
                                    }
                                    const building = buildings.find((building) => building?.studioBuildingId === buildingId);
                                    return building?.name?.[language] || building?.name?.['ko'] || '';
                                }
                            )}
                            startFloorName={server?.floor?.name?.[i18n.language] || server?.floor?.name?.['ko'] || ''}
                            endPoint={generateNavigationGuideEndPoint}
                            onStop={handleStopSimulation}
                            onResume={handleResumeSimulation}
                            onPause={handlePauseSimulation}
                            onWatchAgain={handleWatchAgain}
                        />
                    </div>
                )}
            </section>

            {modalState.poiList && (
                <PoiListPopup
                    isOpen={modalState.poiList}
                    onClose={() => {
                        handleSetModalState({ poiList: false });
                    }}
                    mapData={mapData}
                    currentFloorName={selectedFloorName}
                    handlePoiClick={(data) => {
                        handlePoiClick(data);
                    }}
                />
            )}
            {modalState.noPoiCategory && (
                <NoCategoryPoiPopup
                    isOpen={modalState.noPoiCategory}
                    onClose={async () => {
                        restoreFocus();
                        handleSetModalState({ noPoiCategory: false });
                    }}
                    category={selectedCategory?.name?.[i18n.language] || selectedCategory?.name?.['ko']}
                    building={handleGetBuildingNameById(
                        (useMultiBuildingCategoryLogic && state.nearestPoiFloor && isStoreFloorWithBuilding(state.nearestPoiFloor)
                            ? state.nearestPoiFloor.buildingId
                            : undefined) ||
                            currentMapBuildingId ||
                            currentBuilding?.studioBuildingId ||
                            ''
                    )}
                    floorName={
                        useMultiBuildingCategoryLogic && state.nearestPoiFloor && isStoreFloorWithBuilding(state.nearestPoiFloor)
                            ? getFloorLocalizedName(state.nearestPoiFloor, i18n.language) || getStoreFloorKoName(state.nearestPoiFloor)
                            : (state.nearestPoiFloor as IDataFloor)?.name
                    }
                    onGoToFloor={handleGoToFloor}
                />
            )}

            {/* facility */}
            {poiDetailModalState.facilityPoi && (
                <FacilityPoiDetailModal
                    isOpen={poiDetailModalState.facilityPoi}
                    isShowLocationConfirmation={false}
                    poiDetail={facilityPoiState}
                    onClose={() => {
                        handleSetPoiDetailModalState({ facilityPoi: false });
                        if (state.shouldReopenPoiList) {
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetModalState({ poiList: true });
                        }
                    }}
                    onDirectionGuide={() => {
                        handleStartGuide(facilityPoiState.poiName);
                        handleSetState({ shouldReopenPoiList: false });
                        handleSetPoiDetailModalState({ facilityPoi: false });
                        handleSetModalState({ poiList: false });
                    }}
                />
            )}

            {/* shop */}
            {poiDetailModalState.shopPoi && (
                <PoiDetailModal
                    isOpen={poiDetailModalState.shopPoi}
                    showLocationConfirmation={false}
                    onClose={() => {
                        handleSetPoiDetailModalState({ shopPoi: false });
                        if (state.shouldReopenPoiList) {
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetModalState({ poiList: true });
                        }
                    }}
                    selectedPoi={shopPoiState}
                    onGoToPoiLocation={() => {
                        handleStartGuide(shopPoiState.name);
                        handleSetState({ shouldReopenPoiList: false });
                    }}
                />
            )}

            {/* fnb */}
            {poiDetailModalState.fnbPoi && (
                <ReviewMenuHorizontal
                    isOpen={poiDetailModalState.fnbPoi}
                    showLocationCheck={false}
                    onClose={() => {
                        handleSetPoiDetailModalState({ fnbPoi: false });
                        if (state.shouldReopenPoiList) {
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetModalState({ poiList: true });
                        }
                    }}
                    menuList={fnbMenuList}
                    reviewList={fnbReviewList}
                    poiDetails={fnbPoiState}
                    isLoading={loadingFnb}
                    onGuideStart={() => {
                        handleStartGuide(fnbPoiState.name);
                        handleSetState({ shouldReopenPoiList: false });
                    }}
                />
            )}

            {/* content */}
            {poiDetailModalState.contentPoi && (
                <PoiDetailModal
                    isOpen={poiDetailModalState.contentPoi}
                    onClose={() => {
                        handleSetPoiDetailModalState({ contentPoi: false });
                        if (state.shouldReopenPoiList) {
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetModalState({ poiList: true });
                        }
                    }}
                    selectedPoi={shopPoiState}
                    showLocationConfirmation={false}
                    poiList={relatedPoiContentList}
                    onSelectPoi={(poi) => {
                        setSelectedPoiContent(poi);
                        setDestinationDisplayName(poi?.name?.[language] || poi?.name?.['ko'] || '');
                    }}
                    onGoToPoiLocation={() => {
                        handleStartGuide(shopPoiState.name);
                        handleSetState({ shouldReopenPoiList: false });
                    }}
                />
            )}

            {/* no poi content */}
            {poiDetailModalState.noPoiContent && (
                <EmptyCategoryPoiItem isOpen={poiDetailModalState.noPoiContent} onClose={() => handleSetPoiDetailModalState({ noPoiContent: false })} />
            )}

            {modalState.selectTransportationMode && isNonEmptyObject(routes) && (
                <SelectTransportationModeModal
                    isOpen={modalState.selectTransportationMode}
                    onClose={async () => {
                        handleSetModalState({ selectTransportationMode: false });
                        handleSetState({ navigationStatus: NavigationStatus.IDLE });
                        setRoutes(undefined);
                        setSelectedRoute(undefined);
                        resetSelectedPoi();
                        resetFloorGuideType();
                        await handleShowMyLocation();
                    }}
                    onSelect={handleSelectTransportationMode}
                    routes={isNonEmptyObject(routes) ? (Object.keys(routes) as TRANSPORTATION_REQUEST_TYPE[]) : undefined}
                />
            )}
            {loading && <ContentLoading loading={loading} />}
        </section>
    );
}
